// File: frontend/src/lib/api/notificationService.ts
// Fixed notification service with proper push notification support

import { BaseApiService } from './baseService';
import { ApiResponse } from '../httpClient';

export interface Notification {
  notification_id: number;
  user_id: number;
  type: 'booking' | 'system' | 'payment' | 'review' | 'verification' | 'general';
  title: string;
  message: string;
  data?: any;
  read_at?: string;
  created_at: string;
  updated_at: string;
  action_url?: string;
  action_text?: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  expires_at?: string;
}

export interface NotificationFilters {
  page?: number;
  limit?: number;
  type?: string;
  read?: boolean;
  priority?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
}

export interface NotificationPreferences {
  email_notifications: boolean;
  sms_notifications: boolean;
  push_notifications: boolean;
  booking_updates: boolean;
  promotional_emails: boolean;
  security_alerts: boolean;
  marketing_communications: boolean;
}

export interface NotificationTemplate {
  template_id: number;
  name: string;
  subject: string;
  body: string;
  type: string;
  variables: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface PushSubscription {
  token: string;
  device_type: 'web' | 'android' | 'ios';
  user_agent?: string;
  endpoint: string;
}

class NotificationService extends BaseApiService {
  constructor() {
    super({
      baseEndpoint: '/notifications',
      timeout: 10000,
    });
  }

  // Basic notification operations
  async getNotifications(filters: NotificationFilters = {}): Promise<ApiResponse<{
    notifications: Notification[];
    pagination: any;
  }>> {
    return this.get('', { params: filters });
  }

  async getNotificationById(id: number): Promise<ApiResponse<{ notification: Notification }>> {
    return this.get(`/${id}`);
  }

  async getUnreadNotifications(): Promise<ApiResponse<{
    notifications: Notification[];
    count: number;
  }>> {
    return this.get('/unread');
  }

  async getNotificationCount(): Promise<ApiResponse<{
    total: number;
    unread: number;
    by_type: Record<string, number>;
  }>> {
    return this.get('/count');
  }

  async markAsRead(id: number): Promise<ApiResponse<{ notification: Notification }>> {
    return this.post(`/${id}/read`);
  }

  async markAsUnread(id: number): Promise<ApiResponse<{ notification: Notification }>> {
    return this.post(`/${id}/unread`);
  }

  async markAllAsRead(): Promise<ApiResponse<{ updated_count: number }>> {
    return this.post('/mark-all-read');
  }

  async deleteNotification(id: number): Promise<ApiResponse<void>> {
    return this.delete(`/${id}`);
  }

  async bulkMarkAsRead(notificationIds: number[]): Promise<ApiResponse<{ updated_count: number }>> {
    return this.post('/bulk-read', { notification_ids: notificationIds });
  }

  async bulkDeleteNotifications(notificationIds: number[]): Promise<ApiResponse<{ deleted_count: number }>> {
    return this.post('/bulk-delete', { notification_ids: notificationIds });
  }

  // Notification preferences
  async getNotificationPreferences(): Promise<ApiResponse<NotificationPreferences>> {
    return this.get('/preferences');
  }

  async updateNotificationPreferences(preferences: NotificationPreferences): Promise<ApiResponse<NotificationPreferences>> {
    return this.put('/preferences', preferences);
  }

  // Send notifications (Admin only)
  async sendNotification(
    recipients: number[] | 'all' | 'admins' | 'providers' | 'customers',
    title: string,
    message: string,
    type?: string,
    data?: any,
    actionUrl?: string,
    actionText?: string,
    priority?: 'low' | 'medium' | 'high' | 'urgent'
  ): Promise<ApiResponse<{ sent_count: number }>> {
    return this.post('/send', {
      recipients,
      title,
      message,
      type,
      data,
      action_url: actionUrl,
      action_text: actionText,
      priority,
    });
  }

  // Template operations
  async getNotificationTemplates(): Promise<ApiResponse<{ templates: NotificationTemplate[] }>> {
    return this.get('/templates');
  }

  async createNotificationTemplate(template: Omit<NotificationTemplate, 'template_id' | 'created_at' | 'updated_at'>): Promise<ApiResponse<{ template: NotificationTemplate }>> {
    return this.post('/templates', template);
  }

  async updateNotificationTemplate(templateId: number, template: Partial<NotificationTemplate>): Promise<ApiResponse<{ template: NotificationTemplate }>> {
    return this.put(`/templates/${templateId}`, template);
  }

  async deleteNotificationTemplate(templateId: number): Promise<ApiResponse<void>> {
    return this.delete(`/templates/${templateId}`);
  }

  // Push notification operations - FIXED
  async subscribeToPushNotifications(subscription: PushSubscription): Promise<ApiResponse<{ subscription_id: string }>> {
    return this.post('/push-subscribe', subscription);
  }

  async unsubscribeFromPushNotifications(endpoint: string): Promise<ApiResponse<void>> {
    return this.post('/push-unsubscribe', { endpoint });
  }

  // Alternative method names for backward compatibility
  async registerPushToken(token: string, deviceType: 'web' | 'android' | 'ios' = 'web'): Promise<ApiResponse<{ subscription_id: string }>> {
    return this.subscribeToPushNotifications({
      token,
      device_type: deviceType,
      user_agent: navigator.userAgent,
      endpoint: token
    });
  }

  async removePushToken(token: string): Promise<ApiResponse<void>> {
    return this.unsubscribeFromPushNotifications(token);
  }

  // Testing and analytics
  async testNotification(
    channel: 'email' | 'sms' | 'push' | 'in_app',
    recipient: string,
    template: string
  ): Promise<ApiResponse<{ test_result: string }>> {
    return this.post('/test', { channel, recipient, template });
  }

  async getNotificationStats(period: string = 'monthly'): Promise<ApiResponse<{
    total_sent: number;
    delivery_rate: number;
    open_rate: number;
    click_rate: number;
    by_type: Record<string, number>;
    by_channel: Record<string, number>;
  }>> {
    return this.get('/stats', { params: { period } });
  }

  // Settings and configuration
  async getNotificationSettings(): Promise<ApiResponse<any>> {
    return this.get('/settings');
  }

  async updateNotificationSettings(settings: any): Promise<ApiResponse<any>> {
    return this.put('/settings', settings);
  }

  async getNotificationChannels(): Promise<ApiResponse<{
    channels: Array<{
      name: string;
      enabled: boolean;
      status: 'active' | 'inactive' | 'error';
      last_used: string;
    }>;
  }>> {
    return this.get('/channels');
  }

  // Scheduled notifications
  async scheduleNotification(
    recipients: number[] | string,
    title: string,
    message: string,
    scheduledFor: string,
    type?: string,
    data?: any
  ): Promise<ApiResponse<{ scheduled_id: string }>> {
    return this.post('/schedule', {
      recipients,
      title,
      message,
      scheduled_for: scheduledFor,
      type,
      data,
    });
  }

  // Get user's push subscriptions
  async getUserPushSubscriptions(): Promise<ApiResponse<{ subscriptions: PushSubscription[] }>> {
    return this.get('/push-subscriptions');
  }

  // Update push subscription
  async updatePushSubscription(subscriptionId: string, updates: Partial<PushSubscription>): Promise<ApiResponse<{ subscription: PushSubscription }>> {
    return this.put(`/push-subscriptions/${subscriptionId}`, updates);
  }
}

export const notificationService = new NotificationService();